import React, { useState } from 'react';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { ActionModal } from 'components/ui';
import { AlertIcon, MedicalInfoIcon, PlayIcon, UndoIcon } from 'assets/icons';
import { BaseButton } from 'components/ui/buttons';
import { PatientRecord } from 'record/patient.record';
import { useRestorePatient } from 'hooks/useRestorePatient';
import { StabilityLevel } from 'components/ui/StabilityLevel';
import { AppRoutes, replaceRouteParamWithValue } from 'routes/app-routes';
import { StabilityIcon } from 'components/assessment-details/StabilityIcon';
import { useLoadingWithErrorHandling } from 'hooks/useLoadingWithErrorHandling';
import { StopAssessmentDate } from 'components/assessment/common/StopAssessmentDate';
import { AssignPatientButton } from 'components/assessment-details/AssignPatientButton';
import { MessageModal } from './ui/modal/MessageModal';

type PatientCardProps = {
  patient: PatientRecord;
  isActive?: boolean;
  isYourPatient?: boolean;
  fetchPatients?: () => void;
  showNurse?: boolean;
};

export const PatientCard: React.FC<PatientCardProps> = ({
  patient,
  showNurse = false,
  isActive = true,
  isYourPatient,
  fetchPatients,
}) => {
  const { restorePatient, isShowRestoreModal, setIsShowRestoreModal, submitting } = useRestorePatient(
    patient,
    fetchPatients,
  );
  const { ErrorModalComponent } = useLoadingWithErrorHandling();
  const navigate = useNavigate();
  const [isShowInfantTooYoungModal, setIsShowInfantTooYoungModal] = useState(false);

  const routeToPatientAssessmentDetails = () => {
    if (!patient.id) return;

    const route = replaceRouteParamWithValue(AppRoutes.CurrentPatient, 'id', patient.id);
    navigate(route);
  };

  const handleRouteAssessment = () => {
    if (!patient.id) return;

    if (patient.getAgeInHours() < 4) {
      setIsShowInfantTooYoungModal(true);
      return;
    }

    const route = replaceRouteParamWithValue(
      patient.overallScoreFromLastAssessment ? AppRoutes.CurrentPatient : AppRoutes.Assessment,
      'id',
      patient.id,
    );

    navigate(route, { state: { isCallAssessment: !!patient.overallScoreFromLastAssessment } });
  };

  return (
    <div className="w-full min-h-[100px] flex flex-wrap sm:flex-nowrap items-center justify-between py-3 px-4 bg-white-400 dark:bg-gray-900 border-b-[1px] last:border-none border-white-400/10 gap-y-3">
      <div className="flex w-full sm:w-1/3 gap-2.5">
        <div
          className={cn(
            'flex w-12 h-12 justify-center items-center shrink-0 bg-white-500 dark:bg-white-400/[.05] rounded-2xl',
            showNurse && 'w-16 h-16',
          )}
        >
          <StabilityIcon levelDirection={patient.getAssessmentLevelDirection()} />
        </div>
        <div className="flex flex-col gap-1 justify-center">
          <h3
            className={cn(
              'text-sm font-semibold leading-[140%]',
              isActive ? 'text-gray-900 dark:text-white-400' : 'text-sky dark:text-gray-800',
            )}
          >
            {patient.refId}
          </h3>
          <p className="text-sky dark:text-gray-800 text-xs font-medium leading-[17px]">{patient.getAge()}</p>
          {showNurse && (
            <div className="nurse flex text-nowrap  items-center gap-1.5 text-gray-500 dark:text-gray-200 text-xs font-medium  ">
              <MedicalInfoIcon />
              {patient.assignedUserName}
            </div>
          )}
        </div>
      </div>

      {patient.overallScoreFromLastAssessment && (
        <div className="stability-level sm:w-1/3">
          <StabilityLevel
            isActive={isActive}
            level={patient.overallScoreFromLastAssessment}
            levelDirection={patient.getAssessmentLevelDirection()}
          />
        </div>
      )}

      <div className="flex w-full sm:w-1/3 gap-3 justify-end">
        <BaseButton className="w-full sm:w-auto" scheme="secondary" onClick={() => routeToPatientAssessmentDetails()}>
          Details
        </BaseButton>
        {isActive ? (
          isYourPatient ? (
            <BaseButton
              className="w-full sm:w-auto"
              icon={PlayIcon}
              isIconOnLeftSide
              onClick={() => handleRouteAssessment()}
            >
              Start
            </BaseButton>
          ) : (
            <AssignPatientButton
              className="w-full sm:w-auto"
              patient={patient}
              buttonScheme="stroke"
              afterAssign={fetchPatients}
            />
          )
        ) : (
          <BaseButton
            className="restore-patient border-2 w-full sm:w-auto"
            scheme="secondary"
            onClick={() => setIsShowRestoreModal(true)}
            icon={UndoIcon}
            isIconOnLeftSide
          >
            Restore
          </BaseButton>
        )}
      </div>
      {isShowRestoreModal && (
        <ActionModal
          title={`Are you sure you want to restore patient ID: ${patient.refId}?`}
          description="After restoring, the patient will re-appear in your active roster."
          additionalComponent={<StopAssessmentDate date={patient.assessments_stopped_at} />}
          onCancelClick={() => setIsShowRestoreModal(false)}
          onSuccessClick={restorePatient}
          successText="Yes, restore and assign"
          isSubmitting={submitting}
        />
      )}

      {isShowInfantTooYoungModal && (
        <MessageModal
          icon={AlertIcon}
          iconProps={{ className: 'text-silver-400' }}
          successText="Ok"
          title="Infant Too Young for Assessment"
          description="The first assessment can only be performed when the infant is at least 4 hours old and fully transitioned. Please wait until the recommended time before starting the assessment."
          onCancelClick={() => setIsShowInfantTooYoungModal(false)}
          onSuccessClick={() => {
            setIsShowInfantTooYoungModal(false);
          }}
          isShownCloseButton={false}
        />
      )}

      {ErrorModalComponent}
    </div>
  );
};
